import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui/textarea";
import {
  AlertTriangle,
  ArrowLeft,
  Download,
  Upload,
  CheckCircle,
  FileText,
  Settings,
  X,
  Loader2
} from "lucide-react";
import { useToast } from "../../../../hooks/use-toast";
import { SettingProps } from "../interface";
import binaryApi from "../../../../utils/binaryApi";
import { NGROK } from "../../../../APIs";
import { myLocalStorage } from "../../../StorageHelper";
import useUserStore from "../../../../services/userStore";
import { awsConfig, awsFetchConfig, awsUnConfig, triggerAwsOnDemandSync } from "../Api/settingsApi";
import useCloudSyncWatcher from "../../../../services/useCloudSyncWatcher";
import { refreshCloudCredentialStatuses, useCloudCredentialExpiry } from "../../../../services/useCloudCredentialStore";
import { CloudCredentialExpiredIcon } from "../../CloudCredentialExpired";
import { SiAmazon } from "react-icons/si";

interface AWSSetupData {
  name: string;
  description: string;
  arnId: string;
  externalId: string;
  isConfigured: boolean;
  accountId: string;
}

export default function AWSSettings({ onBack }: SettingProps) {
  const [awsData, setAwsData] = useState<AWSSetupData>({
    name: "",
    description: "",
    arnId: "",
    externalId: "",
    accountId: "",
    isConfigured: false
  });
  const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";

  useCloudSyncWatcher("aws", tenantName);
  const userData = useUserStore((state) => state.user);
  const [step, setStep] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isUnconfiguring, setIsUnconfiguring] = useState(false);
  const { toast } = useToast();
  const credentialExpiry = useCloudCredentialExpiry("aws", tenantName);
  const credentialsExpired = awsData.isConfigured && credentialExpiry.expired;

  const handleFieldChange = (field: keyof AWSSetupData, value: string) => {
    setAwsData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDownloadSetup = async () => {
    if (!awsData.name || !awsData.description) {
      toast({
        title: "Missing Information",
        description: "Please provide both Name and Description before downloading the setup file.",
        variant: "destructive"
      });
      return;
    }

    const response = await binaryApi.get(
      `${NGROK}/api/aws-initial-setup?tenantName=${tenantName}`,
      {
        params: {
          name: awsData.name,
          description: awsData.description,
          arn: "",
        },
        responseType: "blob",
      },
    );



    const blob = new Blob([response.data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'aws-setup.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setStep(2);
    toast({
      title: "Setup File Downloaded",
      description: "aws-setup.json has been downloaded. Please use this file for CloudFormation setup.",
    });
  };

  const handleSubmitInfo = async () => {
    if (!awsData.arnId || !awsData.externalId) {
      toast({
        title: "Missing Information",
        description: "Please provide both ARN ID and External ID.",
        variant: "destructive"
      });

      return;
    }

    setIsLoading(true);
    try {
      let payload = {
        tenantName: tenantName,
        awsARN: awsData.arnId,
        externalId: awsData.externalId,
      }
      await awsConfig(tenantName, payload)
      refreshCloudCredentialStatuses(tenantName);
      const syncResult = await triggerAwsOnDemandSync(tenantName);
      toast({
        title: syncResult?.credentialsExpired ? "AWS Role Not Accessible" : "AWS Configuration Complete",
        variant: syncResult?.credentialsExpired ? "destructive" : undefined,
        description: syncResult?.credentialsExpired
          ? "AWS is configured, but the cross-account role could not be assumed. Check the role ARN and external ID."
          : syncResult?.alreadyRunning
          ? "AWS is configured. A sync is already running."
          : syncResult?.timedOut
            ? "AWS is configured. Initial sync is running in the background."
            : "AWS is configured. Initial resource sync has started.",
      });
      setAwsData(prev => ({ ...prev, isConfigured: true }));
      setStep(6);
    } catch {
      toast({
        title: "Error",
        description: "Error Configuring AWS Setup",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnconfigure = async () => {
    if (isUnconfiguring) {
      return;
    }

    setIsUnconfiguring(true);
    try {
      const roleName = awsData.arnId?.split("/")[1];
      await awsUnConfig(tenantName, roleName);
      refreshCloudCredentialStatuses(tenantName);
      setAwsData({
        name: "",
        description: "",
        arnId: "",
        externalId: "",
        accountId: "",
        isConfigured: false
      });
      setStep(1);
      toast({
        title: "AWS Configuration Removed",
        description: "Your AWS integration has been unconfigured.",
      });
    } catch (e) {
      toast({
        title: "Error",
        variant: "destructive",
        description: "Failed to unconfigure AWS. Please try again.",
      });
    } finally {
      setIsUnconfiguring(false);
    }
  };
  const fetchAWSConfig = async () => {
    try {
      const response = await awsFetchConfig(tenantName);

      if (response) {
        const { crossAccountArn, crossAccountExternalId, accountId } = response;

        const isConfigured = Boolean(crossAccountArn && crossAccountExternalId);

        setAwsData({
          name: accountId || "",
          accountId: accountId || "",
          description: "",
          arnId: crossAccountArn || "",
          externalId: crossAccountExternalId || "",
          isConfigured,
        });
      }
    } catch (error) {
      console.error("Failed to fetch AWS config:", error);
    }
  };

  useEffect(() => {
    fetchAWSConfig();
  }, [tenantName, userData.email])

  const canDownload = awsData.name && awsData.description;

  return (
    <div className="tenant-settings-page flex-1 space-y-6 p-6">
      {/* Header */}
      <Card className="settings-header-card border-border/40 rounded-2xl">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="settings-back-button"
              onClick={onBack}
              aria-label="Back to settings"
              title="Back to settings"
            >

              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="settings-icon-frame w-12 h-12 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900 dark:to-orange-800 rounded-xl flex items-center justify-center border border-orange-200 dark:border-orange-700">
              <SiAmazon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-3" data-testid="text-page-title">
                AWS Setup
                {credentialsExpired && <CloudCredentialExpiredIcon reason={credentialExpiry.reason} />}
              </h1>
              <p className="text-muted-foreground">
                Configure Amazon Web Services integration
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Setup Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* AWS Initial Setup */}
          <Card className="border-border/40 rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                AWS Initial Setup
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {awsData.isConfigured ? <>
                <div className="space-y-2">
                  <Label htmlFor="aws-name">ARN ID</Label>
                  <Input
                    id="aws-name"
                    value={awsData.arnId}
                    disabled
                    placeholder="Enter AWS configuration name"
                    data-testid="input-aws-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="aws-description">External Id</Label>
                  <Input
                    id="aws-name"
                    value={awsData.externalId}
                    disabled
                    placeholder="Enter AWS configuration name"
                    data-testid="input-aws-name"
                  />
                </div>

              </> : <>
                <div className="space-y-2">
                  <Label htmlFor="aws-name">Name</Label>
                  <Input
                    id="aws-name"
                    value={awsData.name}
                    onChange={(e) => handleFieldChange("name", e.target.value)}
                    placeholder="Enter AWS configuration name"
                    data-testid="input-aws-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="aws-description">Description</Label>
                  <Textarea
                    id="aws-description"
                    value={awsData.description}
                    onChange={(e) => handleFieldChange("description", e.target.value)}
                    placeholder="Describe this AWS configuration"
                    rows={3}
                    data-testid="textarea-aws-description"
                  />
                </div>

              </>}


              <div className="pt-4">
                <div className="settings-dropzone flex items-center gap-3 p-4 border-2 border-dashed border-border rounded-lg bg-muted/30">
                  <FileText className="w-8 h-8 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="font-medium text-sm">aws-setup.json</p>
                    <p className="text-xs text-muted-foreground">
                      {canDownload ? "Ready to download" : "Complete the form above to enable download"}
                    </p>
                  </div>
                  <Button
                    onClick={handleDownloadSetup}
                    disabled={!canDownload}
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    data-testid="button-download-setup"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ARN and External ID Submission */}
          {step >= 2 && (
            <Card className="border-border/40 rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  Submit ARN and External ID
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="arn-id">Role ARN</Label>
                  <Input
                    id="arn-id"
                    value={awsData.arnId}
                    onChange={(e) => handleFieldChange("arnId", e.target.value)}
                    placeholder="arn:aws:iam::123456789012:role/YourRoleName"
                    data-testid="input-arn-id"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="external-id">External ID</Label>
                  <Input
                    id="external-id"
                    value={awsData.externalId}
                    onChange={(e) => handleFieldChange("externalId", e.target.value)}
                    placeholder="Enter the External ID from CloudFormation output"
                    data-testid="input-external-id"
                  />
                </div>

                <div className="pt-4">
                  <Button
                    onClick={handleSubmitInfo}
                    className="gap-2"
                    data-testid="button-submit-info"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    {isLoading ? "Submitting..." : "Submit Info"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Confirmation */}
          {awsData.isConfigured && (
            <Card className={`rounded-2xl ${credentialsExpired
              ? "border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950"
              : "border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950"}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  {credentialsExpired ? (
                    <div className="flex items-center gap-3" data-testid="banner-credentials-expired">
                      <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 shrink-0" />
                      <div>
                        <h3 className="font-semibold text-red-900 dark:text-red-100">
                          AWS Credentials Expired
                        </h3>
                        <p className="text-sm text-red-700 dark:text-red-300">
                          {credentialExpiry.reason ? `${credentialExpiry.reason}. ` : ""}Log collection and sync are paused
                          until the cross-account role can be assumed again. Restore the role, or unconfigure and set up the
                          account again.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                      <div>
                        <h3 className="font-semibold text-green-900 dark:text-green-100">
                          Configuration Complete
                        </h3>
                        <p className="text-sm text-green-700 dark:text-green-300">
                          Your AWS integration has been successfully configured and is ready to use.
                        </p>
                      </div>
                    </div>
                  )}
                  <Button
                    onClick={handleUnconfigure}
                    variant="destructive"
                    size="sm"
                    className="gap-2"
                    disabled={isUnconfiguring}
                    data-testid="button-unconfigure-aws"
                  >
                    {isUnconfiguring ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <X className="w-4 h-4" />
                    )}
                    {isUnconfiguring ? "Unconfiguring..." : "Unconfigure AWS Account"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Setup Instructions */}
        <div className="space-y-6">
          <Card className="border-border/40 rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Setup Instructions
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Follow these steps to configure your AWS account:
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className={`flex gap-3 ${step >= 1 ? 'opacity-100' : 'opacity-50'}`}>
                  <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-xs font-semibold text-blue-600 dark:text-blue-400">
                    1
                  </div>
                  <div>
                    <h4 className="font-medium text-sm">Provide Name and Description</h4>
                    <p className="text-xs text-muted-foreground">
                      Enter the Name and Description below. Once both fields are filled, the download option will be enabled.
                    </p>
                  </div>
                </div>

                <div className={`flex gap-3 ${step >= 2 ? 'opacity-100' : 'opacity-50'}`}>
                  <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-xs font-semibold text-blue-600 dark:text-blue-400">
                    2
                  </div>
                  <div>
                    <h4 className="font-medium text-sm">Download aws-setup.json</h4>
                    <p className="text-xs text-muted-foreground">
                      Click the download icon to obtain the 'aws-setup.json' file. This file will be used for the next step.
                    </p>
                  </div>
                </div>

                <div className={`flex gap-3 ${step >= 3 ? 'opacity-100' : 'opacity-50'}`}>
                  <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-xs font-semibold text-blue-600 dark:text-blue-400">
                    3
                  </div>
                  <div>
                    <h4 className="font-medium text-sm">Configure AWS in CloudFormation</h4>
                    <p className="text-xs text-muted-foreground">
                      Log into your AWS Management Console and navigate to CloudFormation. Use the downloaded file to create a new CloudFormation stack.
                    </p>
                  </div>
                </div>

                <div className={`flex gap-3 ${step >= 4 ? 'opacity-100' : 'opacity-50'}`}>
                  <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-xs font-semibold text-blue-600 dark:text-blue-400">
                    4
                  </div>
                  <div>
                    <h4 className="font-medium text-sm">Retrieve External ID and Role ARN</h4>
                    <p className="text-xs text-muted-foreground">
                      After the CloudFormation stack is successfully created, copy the External ID and Role ARN from the stack's output.
                    </p>
                  </div>
                </div>

                <div className={`flex gap-3 ${step >= 5 ? 'opacity-100' : 'opacity-50'}`}>
                  <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-xs font-semibold text-blue-600 dark:text-blue-400">
                    5
                  </div>
                  <div>
                    <h4 className="font-medium text-sm">Submit the ARN and External ID</h4>
                    <p className="text-xs text-muted-foreground">
                      Return to this page and provide the ARN ID and External ID below. Click "Submit Info" to finalize the configuration.
                    </p>
                  </div>
                </div>

                <div className={`flex gap-3 ${step >= 6 ? 'opacity-100' : 'opacity-50'}`}>
                  <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center text-xs font-semibold text-green-600 dark:text-green-400">
                    ✓
                  </div>
                  <div>
                    <h4 className="font-medium text-sm">Confirmation</h4>
                    <p className="text-xs text-muted-foreground">
                      Upon successful submission, you will receive a confirmation message.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
